import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useInitData, useNotification } from '../../../../hooks'
import { isDraftCreateNewAction, useDraftCreationOpener } from '../../../Draft'
import { RequestDraftCreateActionSelectModal } from '../../RequestDraftCreateActionSelectModal'
import {
  getRequestFormErrorNotification,
  getRequestFormSuccessNotification,
} from '../../RequestForm'
import { RequestFormModal } from '../../RequestFormModal'
import { RequestWizardFormModal } from '../../RequestWizardFormModal'
import { useRequestWizardDraftSession } from '../useRequestWizardDraftSession'

import {
  getCreateRequestPayloadInitialValues,
  getCreateRequestSubmitPayload,
} from './useCreateRequest.utils'

export function useCreateRequest({
  minimizedModalScope,
  setModal: externalSetModal,
  onClose,
  onReload,
} = EMPTY_OBJECT) {
  const { t } = useTranslation()

  const { availableSections, is_wizard_enabled: isWizardEnabled } =
    useInitData()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  // #TODO Get rid of modal states after global modal provider implementation
  const [modal, setModal] = useState(null)

  const {
    draft: activeDraft,
    draftId: activeDraftId,
    handleCreateNew,
    handleOpenDraft,
    minimizeKey,
  } = useRequestWizardDraftSession({ isWizardEnabled })

  function handleSetModal(value) {
    const setter = externalSetModal ?? setModal

    setter(value)
  }

  function closeModal() {
    return handleSetModal(null)
  }

  function handleClose() {
    handleSetModal(null)

    onClose?.()
  }

  function handleErrorCreation() {
    handleAddErrorNotification(getRequestFormErrorNotification(t))
  }

  function handleSuccess(data) {
    handleClose()

    handleAddSuccessNotification(
      getRequestFormSuccessNotification({
        availableSections,
        data,
        t,
      })
    )

    onReload?.()
  }

  function handleWizardSuccess(createdRequests, onSuccess) {
    createdRequests.forEach(createdRequest => {
      handleAddSuccessNotification(
        getRequestFormSuccessNotification({
          availableSections,
          data: createdRequest,
          t,
        })
      )
    })

    onReload?.()

    onSuccess?.()
  }

  function handleFormSuccess(data, onSuccess) {
    handleSuccess(data)

    onSuccess?.(data)
  }

  function openModal({ action, formProps, wizardProps } = EMPTY_OBJECT) {
    handleSetModal(
      isWizardEnabled ? (
        <RequestWizardFormModal
          {...wizardProps}
          autoRestore={action != null && !isDraftCreateNewAction(action)}
          minimizedModalScope={minimizedModalScope}
          onClose={handleClose}
          onError={handleErrorCreation}
          onSuccess={createdRequests =>
            handleWizardSuccess(createdRequests, wizardProps?.onSuccess)
          }
        />
      ) : (
        <RequestFormModal
          {...formProps}
          minimizedModalScope={minimizedModalScope}
          onClose={handleClose}
          onError={handleErrorCreation}
          onSuccess={data => handleFormSuccess(data, formProps?.onSuccess)}
        />
      )
    )
  }

  const handleOpenCreation = useDraftCreationOpener({
    DraftPickerModal: RequestDraftCreateActionSelectModal,
    closeModal,
    draft: activeDraft,
    draftId: activeDraftId,
    getInitialValues: getCreateRequestPayloadInitialValues,
    getSubmitPayload: getCreateRequestSubmitPayload,
    minimizeKey,
    openModal,
    setModal: handleSetModal,
    onCreateNew: handleCreateNew,
    onOpenDraft: handleOpenDraft,
  })

  return {
    handleOpenCreation,
    isWizardEnabled,
    requestCreationModal: modal,
  }
}
